'use client';
import React, { useCallback, useId, useRef } from 'react';
import { Label, LabelWrapper, RadioInput, Slider, SwitchContainer } from './styled';

type SwitchItem<T extends string> = {
  label?: string;
  value: T;
};

export type ToggleSwitchProps<T extends string> = {
  items: SwitchItem<T>[];
  onChangeAction?: (value: T) => void;
  disabled?: boolean;
  value?: T;
  className?: string;
  isError?: boolean;
};

export const ToggleSwitch = <T extends string>({
  items,
  onChangeAction = () => {},
  disabled,
  value,
  className,
  isError
}: ToggleSwitchProps<T>) => {
  const labelsRef = useRef<(HTMLLabelElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const groupId = useId();

  const onChangeCallback = useCallback(
    (item: SwitchItem<T>) => {
      onChangeAction(item.value);
    },
    [onChangeAction]
  );

  const toggleNext = useCallback(() => {
    const index = items.findIndex((item) => item.value === value);
    const nextIndex = (index + 1) % items.length;
    onChangeAction(items[nextIndex].value);
  }, [items, onChangeAction, value]);

  return (
    <SwitchContainer
      ref={containerRef}
      className={className}
      $isError={isError}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Space' || e.key === ' ') {
          e.preventDefault();
          toggleNext();
        }
      }}
    >
      {items.map((item, index) => (
        <LabelWrapper key={item.value}>
          <RadioInput
            disabled={disabled}
            type="radio"
            id={`${groupId}-${item.value || 'empty'}`}
            value={item.value}
            checked={value === item.value}
            onChange={() => onChangeCallback(item)}
          />
          {item.value === value && (
            <Slider
              layoutId={'activeTabIndicator' + groupId}
              transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
              $disabled={disabled}
            />
          )}
          <Label
            $disabled={disabled}
            htmlFor={`${groupId}-${item.value || 'empty'}`}
            ref={(el) => {
              labelsRef.current[index] = el;
            }}
            $active={value === item.value}
          >
            {item.label}
          </Label>
        </LabelWrapper>
      ))}
    </SwitchContainer>
  );
};
